import React from "react";
import { getImageLink } from "../../../utils/images";
import { decodeText } from "../../../utils/richText";

// Same parsing as demo-ftl-base's video.ftl: a YouTube/Vimeo URL, or a bare id
// (11 characters = YouTube, digits only = Vimeo).
const parseEmbed = (value = "") => {
    const input = value.trim();
    if (!input) return null;

    const youtube = input.match(/^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (youtube) return { provider: "youtube", id: youtube[1] };

    const vimeo = input.match(/^(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) return { provider: "vimeo", id: vimeo[1] };

    if (/^\d+$/.test(input)) return { provider: "vimeo", id: input };
    if (/^[A-Za-z0-9_-]{11}$/.test(input)) return { provider: "youtube", id: input };

    return null;
};

// Privacy-friendly player URL; autoplay needs mute, YouTube loops only with a playlist of itself.
const getEmbedSrc = ({ provider, id }, autoplay) =>
    provider === "youtube"
        ? `https://www.youtube-nocookie.com/embed/${id}?rel=0${autoplay ? `&autoplay=1&mute=1&loop=1&controls=0&playlist=${id}` : ""}`
        : `https://player.vimeo.com/video/${id}?dnt=1${autoplay ? "&autoplay=1&muted=1&loop=1&background=1" : ""}`;

const fill = { position: "absolute", inset: 0, width: "100%", height: "100%" };

// YouTube/Vimeo embed or a DAM (or Cloudinary) video, the headless sibling of
// demo-ftl-base's Video component.
const Video = ({ source = {}, aspectRatio = "16:9", autoplay, caption }) => {
    // The dialog's source switch: "external" holds a YouTube/Vimeo URL, "internal" a DAM video.
    const isExternal = (source.field || "external") === "external";
    const embed = isExternal ? parseEmbed(source.embedUrl) : null;
    const src = !isExternal && getImageLink(source.video);
    if (!embed && !src) return null;

    const isAutoplay = autoplay === true || autoplay === "true";
    const label = decodeText(caption || "");

    return (
        <figure className="mb-0">
            <div className="position-relative w-100 overflow-hidden rounded-3 bg-dark" style={{ aspectRatio: aspectRatio.replace(":", " / ") }}>
                {embed ? (
                    <iframe
                        style={{ ...fill, border: 0 }}
                        src={getEmbedSrc(embed, isAutoplay)}
                        title={label || `${embed.provider === "youtube" ? "YouTube" : "Vimeo"} video`}
                        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                        allowFullScreen
                        loading="lazy"
                    />
                ) : (
                    <video
                        style={{ ...fill, objectFit: "cover" }}
                        src={src}
                        preload="metadata"
                        {...(isAutoplay ? { autoPlay: true, muted: true, loop: true, playsInline: true } : { controls: true })}
                    />
                )}
            </div>
            {label && <figcaption className="figure-caption mt-2">{label}</figcaption>}
        </figure>
    );
};

export default Video;